'use client';

import {
  forwardRef,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { cn } from '../../lib/cn';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { useIsomorphicLayoutEffect } from '../../lib/isomorphic-layout-effect';
import { usePortalContainer } from '../../lib/portal-container';
import { SCROLLBAR_HIDDEN_CLASS } from '../../lib/scrollbar';
import { SGhostButton } from '../SGhostButton';
import { SIcon } from '../SIcon';
import { SLayoutContext } from '../SLayout/SLayout';
import { STag } from '../STag';
import {
  GNB_HEADER,
  GNB_MENU,
  GNB_COLORS,
  GNB_COMMON,
  GNB_FOLD_MS,
  GNB_EXPAND_MS,
  GNB_FULL_LOGO_WIDTH,
  GNB_FULL_LOGO_WIDTH_NO_LAUNCHER,
  GNB_MENU_MAX_WIDTH,
  GNB_MENU_MIN_WIDTH,
  GNB_MENU_RESIZE_KEY_STEP,
  GNB_MENU_WIDTH,
  GNB_RAIL,
  GNB_RAIL_WIDTH,
  GNB_TOP_ICON_GAP,
  clampGnbMenuWidth,
  findGnbAncestors,
  findGnbRailValue,
  findGnbSiblingValues,
  type SGnbType,
  type SGnbHeader,
  type SGnbColor,
  type SGnbMenuItem,
} from './gnb.config';

export interface SGnbProps extends Omit<HTMLAttributes<HTMLDivElement>, 'color'> {
  /** 메뉴 스타일: box(라운드) / belt(풀폭 행). 미지정 시 SLayout 의 type 을 따른다. */
  type?: SGnbType;
  /**
   * 상단바 구조: fix(GNB 폭 고정) / full(레이아웃 전폭). 미지정 시 SLayout 의 header 를 따른다.
   * 단, useRail 이고 레일에 children 없는 아이템이 있으면 메뉴 폭이 출렁이므로 full 로 강제된다.
   */
  header?: SGnbHeader;
  /** 색상 테마: light / dark */
  color?: SGnbColor;
  /** 메뉴 트리 (depth1 → depth2 → depth3). useRail 이면 첫 depth 가 레일이 되고 그 children 이 depth1 이 된다. */
  items: SGnbMenuItem[];
  /**
   * 좌측 레일 사용 여부. 켜면 items 의 첫 depth 가 아이콘+라벨 버튼의 세로 레일로 서고,
   * 선택된 레일 아이템의 children 이 오른쪽 메뉴 패널에 깔린다. 미지정 시 SLayout 의 useRail 을 따른다.
   * children 없는 레일 아이템이 활성이면 메뉴 패널은 렌더되지 않고 레일만 남는다.
   */
  useRail?: boolean;
  /** 현재 선택된 아이템 value */
  value?: string;
  /** 선택 변경 (sdUpdate) */
  onValueChange?: (value: string) => void;
  /** 레일 선택 변경. 레일 아이템을 눌러 패널이 바뀔 때 알린다(선택 상태는 SGnb 가 자체 관리). */
  onRailChange?: (value: string) => void;
  /** 접힘(레일) 상태. 미지정 시 SLayout 의 folded 를 따른다. */
  folded?: boolean;
  /** 접힘 토글 (sdFoldChange) */
  onFoldChange?: (folded: boolean) => void;
  /** 앱런처(그리드) 버튼 클릭. 미지정 시 런처 버튼을 렌더하지 않는다. 접힘 레일(fix)에는 폴드 버튼만 남으므로 표시되지 않는다. */
  onLauncherClick?: () => void;
  /** 상단바 로고 영역 (slot). header="full" 이면 폭이 140px 로 고정된다. */
  logo?: ReactNode;
  /**
   * 상단바 로고 오른쪽 슬롯 (검색·액션 등). 로고와 16px 띄고 남는 폭을 모두 차지하므로
   * 안에서 자유롭게 정렬한다. 상단바가 전폭인 header="full" 에서만 렌더된다
   * (fix 는 상단바가 좁은 GNB 컬럼 안이라 놓을 자리가 없다).
   */
  topContent?: ReactNode;
  /**
   * 레일 상단 고정 슬롯. 레일 아이템이 많아 넘치면 아이템 목록(ul)만 스크롤되고
   * 이 슬롯은 레일 상단(상단바 바로 아래)에 붙어 고정된다. useRail 일 때만 렌더된다.
   */
  railTop?: ReactNode;
  /**
   * 레일 하단 고정 슬롯. 레일 아이템이 많아 넘치면 아이템 목록(ul)만 스크롤되고
   * 이 슬롯은 레일 하단에 붙어 고정된다. useRail 일 때만 렌더된다.
   */
  railFooter?: ReactNode;
  /**
   * 메뉴 상단 고정 슬롯. 메뉴 아이템이 많아 넘치면 메뉴 목록(ul)만 스크롤되고
   * 이 슬롯은 메뉴 상단에 붙어 고정된다. 메뉴가 렌더될 때만(showMenu) 나타난다.
   */
  menuTop?: ReactNode;
  /**
   * 메뉴 하단 고정 슬롯. 메뉴 아이템이 많아 넘치면 메뉴 목록(ul)만 스크롤되고
   * 이 슬롯은 메뉴 하단에 붙어 고정된다. 메뉴가 렌더될 때만(showMenu) 나타난다.
   */
  menuFooter?: ReactNode;
  /**
   * 접힘(fix 레일) 상단 고정 슬롯. 접으면 본문이 빠져나가며 rail/menu top 도 사라지므로,
   * 48px 폴드 레일 상단(상단바 바로 아래)에 붙는 별도 슬롯이다. header="fix" 로 접혔을 때만 나타난다.
   */
  foldedTop?: ReactNode;
  /**
   * 접힘(fix 레일) 하단 고정 슬롯. 접으면 본문이 빠져나가며 rail/menu footer 도 사라지므로,
   * 48px 폴드 레일 바닥에 붙는 별도 슬롯이다. header="fix" 로 접혔을 때만 나타난다.
   */
  foldedFooter?: ReactNode;
  /**
   * 메뉴 폭을 드래그로 조절할 수 있게 한다. 레일 폭은 고정이고 **메뉴 컬럼만** 늘고 준다.
   * 조절선은 GNB 컬럼의 오른쪽 경계 전체다 — fix 는 상단바 높이까지, full 은 상단바가 전폭이라 본문 높이까지.
   * 접혀 있거나 깔 메뉴가 없으면(레일 리프가 활성) 조절선이 나오지 않는다.
   */
  resizable?: boolean;
  /** 메뉴 폭(px). 주면 controlled — onMenuWidthChange 로 직접 갱신해야 움직인다 */
  menuWidth?: number;
  /** 메뉴 초기 폭(px). uncontrolled */
  defaultMenuWidth?: number;
  /** 메뉴 폭이 확정될 때(드래그를 놓거나 방향키 조작). 드래그하는 동안에는 오지 않는다 */
  onMenuWidthChange?: (width: number) => void;
  /** 메뉴 landmark(nav) 의 접근성 레이블. 한 화면에 nav 가 여럿일 때 구분한다. */
  ariaLabel?: string;
}

/** 잘린 메뉴명 칩이 닫히기까지의 유예. SGhostButton·STooltip 과 같은 값이다. */
const LABEL_TIP_HIDE_DELAY_MS = 100;

const DEPTH_PADDING_LEFT = (M: (typeof GNB_MENU)[SGnbType], depth: number): string =>
  depth === 1 ? M.depth1PaddingLeft : depth === 2 ? M.depth2PaddingLeft : M.depth3PaddingLeft;

/**
 * SGnb — 글로벌 네비게이션.
 * 세 축이 독립이다: type(box/belt) = 메뉴 스타일, header(fix/full) = 상단바 구조,
 * color(light/dark) = 색. 치수는 --cmp-gnb / --cmp-gnb-box / --cmp-gnb-belt, 색은 --cmp-gnb-light / --cmp-gnb-dark 토큰 합성.
 *
 * header 가 배치와 접힘 형태를 정한다:
 * - fix: 상단바가 GNB 컬럼(메뉴 폭) 안에 있다. 접으면 48px 세로 레일로 좁혀지고 상단바에는 폴드 버튼만 남는다.
 * - full: 상단바가 레이아웃 전폭을 가로지르고 그 아래 [메뉴 | 페이지]. 접으면 메뉴만 사라진다.
 *
 * useRail 이면 items 의 첫 depth 가 좌측 레일(아이콘+라벨 68px)로 서고, 선택된 레일 아이템의
 * children 이 메뉴 패널의 depth1 이 된다 — 트리는 그대로 두고 어디부터 그릴지만 바뀐다.
 * children 없는 레일 아이템은 자기 자신이 선택(리프)이라 메뉴 패널을 렌더하지 않는다. 이런 리프가
 * 하나라도 있으면 활성에 따라 메뉴 폭이 출렁이므로 header 는 full 로 강제해 상단바를 전폭에 고정한다.
 *
 * 접힘/펼침은 GNB_FOLD_MS 동안의 슬라이드다 — 레일·메뉴가 함께 왼쪽으로 빠져나가고 펼치면 오른쪽에서 들어온다.
 * 페이지 쪽 폭은 fix 면 이 컴포넌트의 폭 전환이, full 이면 SLayout 의 메뉴 열 전환이 같은 길이로 맞춘다.
 *
 * SLayout 안에서 쓰면 type·header·folded 를 context 에서 읽어 앱이 중복으로 넘길 필요가 없고,
 * 자기 치수(컬럼 폭·상단바 높이·전환 길이)를 registerNav 로 되올려 프레임이 그 값으로 그리드를 깐다.
 *
 * SLayout 없이 단독으로도 동작한다(prop 또는 기본값 사용). 단 full 을 **페이지와 나란히** 놓으려면
 * SLayout 이 필요하다 — 그 안에서만 루트를 display:contents 로 지워 상단바·본문을 프레임 그리드의
 * header/nav 셀에 앉힌다. 단독일 때는 루트가 전폭 세로 상자로 남아 [상단바 / 본문] 을 스스로 쌓는다.
 */
export const SGnb = forwardRef<HTMLDivElement, SGnbProps>(function SGnb(
  {
    type: typeProp,
    header: headerProp,
    color = 'light',
    items,
    useRail: useRailProp,
    value = '',
    onValueChange,
    onRailChange,
    folded: foldedProp,
    onFoldChange,
    onLauncherClick,
    logo,
    topContent,
    railTop,
    railFooter,
    menuTop,
    menuFooter,
    foldedTop,
    foldedFooter,
    resizable = false,
    menuWidth: menuWidthProp,
    defaultMenuWidth,
    onMenuWidthChange,
    ariaLabel = 'global navigation',
    className,
    style,
    ...rest
  },
  ref,
) {
  // 우선순위: prop > SLayout context > 기본값
  const layout = useContext(SLayoutContext);
  const type = typeProp ?? layout?.type ?? 'box';
  const folded = foldedProp ?? layout?.folded ?? false;
  const useRail = useRailProp ?? layout?.useRail ?? false;

  // 레일에 children 없는 아이템이 하나라도 있으면, 그 아이템이 활성일 때 메뉴 패널이 사라져
  // GNB 폭이 [레일+메뉴]↔[레일]로 출렁인다. fix 상단바는 GNB 폭에 묶여 함께 출렁이므로,
  // 이 경우 header 를 full 로 강제해 상단바를 레이아웃 전폭에 붙들어 둔다(요청 prop/context 를 덮는다).
  const railHasLeaf = useRail && items.some(item => !item.children || item.children.length === 0);
  const header = railHasLeaf ? 'full' : (headerProp ?? layout?.header ?? 'fix');

  const H = GNB_HEADER[header];
  const M = GNB_MENU[type];
  const C = GNB_COLORS[color];

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(findGnbAncestors(items, value)),
  );
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  // ── 잘린 메뉴명 칩 ──
  // 폭이 모자라 말줄임된 항목만 hover 시 전체 이름을 칩으로 띄운다(SGhostButton 의 tooltipText 와 같은 형식).
  // 잘렸는지는 hover 시점에 잰다 — resizable 로 폭이 바뀌어도 늘 맞고, 평소엔 아무 비용이 들지 않는다.
  // 팝오버는 GNB 당 하나만 두고 virtualRef 로 hover 한 요소를 가리킨다. 아이템마다 하나씩 두면
  // 메뉴가 큰 화면에서 Root 가 수백 개가 되고, 폭을 끄는 동안 매 프레임 그 비용을 다시 낸다.
  const [labelTip, setLabelTip] = useState<{ label: string; el: HTMLElement } | null>(null);
  const labelTipHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const portalContainer = usePortalContainer();
  /**
   * Radix 의 virtualRef 는 **참조가 바뀔 때만** 위치를 다시 잡고 null 을 받지 않는다.
   * 그래서 현재 라벨을 대신 재 주는 얇은 객체를 두고, hover 대상이 바뀔 때만 새로 만든다.
   */
  const labelTipAnchorRef = useMemo(
    () => ({
      current: {
        getBoundingClientRect: () => labelTip?.el.getBoundingClientRect() ?? new DOMRect(),
      },
    }),
    [labelTip?.el],
  );

  const cancelLabelTipHide = () => {
    if (labelTipHideRef.current === null) return;
    clearTimeout(labelTipHideRef.current);
    labelTipHideRef.current = null;
  };
  /**
   * hover 는 라벨이 아니라 **행 전체**가 받는다. 라벨에만 걸면 하위 메뉴처럼 왼쪽 들여쓰기와
   * 하이픈 자리표시자가 넓은 행에서 커서가 라벨에 닿지 않아 칩이 뜨지 않는다.
   * 잘렸는지는 행 안의 라벨(data-gnb-label)을 찾아 잰다.
   */
  const showLabelTipIfTruncated = (row: HTMLElement, label: string) => {
    cancelLabelTipHide();
    const el = row.querySelector<HTMLElement>('[data-gnb-label]');
    // 잘리지 않았으면 띄울 이유가 없다 — 이미 다 보이는 이름을 한 번 더 보여주는 꼴이다.
    if (!el || el.scrollWidth <= el.clientWidth) {
      setLabelTip(null);
      return;
    }
    setLabelTip(prev => (prev?.el === el && prev.label === label ? prev : { label, el }));
  };
  const startLabelTipHide = () => {
    cancelLabelTipHide();
    labelTipHideRef.current = setTimeout(() => setLabelTip(null), LABEL_TIP_HIDE_DELAY_MS);
  };
  useEffect(() => cancelLabelTipHide, []);

  /** 행 hover — 선택 하이라이트와 잘린 이름 칩을 함께 다룬다. */
  const rowHoverProps = (item: SGnbMenuItem) => ({
    onMouseEnter: (event: ReactMouseEvent<HTMLElement>) => {
      setHoveredKey(item.value);
      showLabelTipIfTruncated(event.currentTarget, item.label);
    },
    onMouseLeave: () => {
      setHoveredKey(prev => (prev === item.value ? null : prev));
      startLabelTipHide();
    },
  });
  // 활성 레일 = 패널에 children 을 깔 첫 depth 아이템. value 가 속한 가지에서 출발하고,
  // 이후엔 레일 클릭으로 바뀐다(선택 없이 다른 가지를 둘러보는 걸 허용).
  const [activeRail, setActiveRail] = useState<string | undefined>(
    () => findGnbRailValue(items, value) ?? items[0]?.value,
  );

  // 선택값/아이템이 바뀌어도 선택 경로가 보이도록 조상을 펼친다 (기존 펼침 유지).
  useEffect(() => {
    const ancestors = findGnbAncestors(items, value);
    if (ancestors.length === 0) return;
    setExpandedKeys(prev => {
      // 이미 다 펼쳐져 있으면 같은 Set 을 반환해 불필요한 리렌더를 막는다.
      if (ancestors.every(a => prev.has(a))) return prev;
      const next = new Set(prev);
      ancestors.forEach(a => next.add(a));
      return next;
    });
  }, [items, value]);

  // 앱이 다른 가지로 이동하면(value 변경) 레일도 그 가지로 따라간다.
  // 어디에도 안 걸리는 value(빈 값 등)면 지금 보던 레일을 유지한다.
  useEffect(() => {
    const owner = findGnbRailValue(items, value);
    if (owner !== undefined) setActiveRail(owner);
  }, [items, value]);

  // items 가 통째로 바뀌어 활성 레일이 사라졌으면 첫 아이템으로 되돌린다(패널이 빈 채 남는 걸 막는다).
  useEffect(() => {
    setActiveRail(prev =>
      prev !== undefined && items.some(item => item.value === prev) ? prev : items[0]?.value,
    );
  }, [items]);

  // 아코디언: 새로 펼치는 아이템의 형제(같은 부모 아래 나머지)가 열려 있으면 함께 닫는다.
  // 같은 depth 에 한 번에 하나만 펼쳐지도록 해 메뉴가 무한정 길어지는 걸 막는다.
  const toggleExpand = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        findGnbSiblingValues(items, key).forEach(sibling => next.delete(sibling));
        next.add(key);
      }
      return next;
    });
  };

  const handleItemClick = (item: SGnbMenuItem) => {
    if (item.disabled) return;
    if (item.children && item.children.length > 0) {
      toggleExpand(item.value);
      return;
    }
    onValueChange?.(item.value);
  };

  // 레일 아이템은 보통 컨테이너라 누르면 패널만 바꾼다.
  // children 이 없는 레일 아이템은 갈 곳이 패널에 없으므로 그 자체를 선택으로 친다.
  const handleRailClick = (item: SGnbMenuItem) => {
    if (item.disabled) return;
    setActiveRail(item.value);
    onRailChange?.(item.value);
    if (!item.children || item.children.length === 0) onValueChange?.(item.value);
  };

  const handleFoldToggle = () => {
    const next = !folded;
    onFoldChange?.(next);
    // folded 를 prop 으로 직접 제어 중이면 context 는 건드리지 않는다 —
    // SLayout 프레임만 접히고 SGnb 는 펼쳐진 채 남는 어긋남을 막는다.
    if (foldedProp === undefined) layout?.setFolded(next);
  };

  // 레일 아이템 — 아이콘 위, 라벨 아래의 44px 세로 버튼.
  // 색은 버튼에 얹어 아이콘·라벨이 함께 물들게 한다(SIcon 은 color 가 없으면 currentColor 상속).
  const renderRailItem = (item: SGnbMenuItem): ReactNode => {
    const isActive = item.value === activeRail;
    const isHovered = hoveredKey === item.value && !item.disabled;

    return (
      <li key={item.value}>
        <button
          type="button"
          disabled={item.disabled}
          // 레일은 '현재 페이지'가 아니라 활성 섹션이라 page 가 아닌 true 로 표시한다
          // (선택된 리프 메뉴가 aria-current="page" 를 따로 갖는다).
          aria-current={isActive ? 'true' : undefined}
          className="box-border flex select-none flex-col items-center border-none bg-transparent transition-colors duration-150"
          style={{
            width: GNB_RAIL.itemWidth,
            paddingTop: GNB_RAIL.itemPaddingTop,
            paddingBottom: GNB_RAIL.itemPaddingBottom,
            borderRadius: GNB_RAIL.itemRadius,
            backgroundColor: isActive
              ? C.itemBgSelected
              : isHovered
                ? C.itemBgHover
                : C.itemBgDefault,
            color: isActive ? C.itemColorActive : C.railFgDefault,
            // <button> 의 UA 폰트를 끊고 앱 폰트를 잇는다 (메뉴 아이템과 같은 이유)
            fontFamily: 'inherit',
            fontSize: GNB_RAIL.itemFontSize,
            lineHeight: `${GNB_RAIL.itemLineHeight}px`,
            fontWeight: isActive ? GNB_RAIL.itemFontWeightSelected : GNB_RAIL.itemFontWeight,
            opacity: item.disabled ? 0.4 : 1,
            cursor: item.disabled ? 'not-allowed' : 'pointer',
          }}
          onClick={() => handleRailClick(item)}
          {...rowHoverProps(item)}
        >
          {item.icon && <SIcon name={item.icon} size={GNB_RAIL.itemIcon} className="shrink-0" />}
          <span
            data-gnb-label
            className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-center"
          >
            {item.label}
          </span>
        </button>
      </li>
    );
  };

  // branchHasIcon: 이 아이템이 속한 depth1 가지에 아이콘이 있는지.
  // 아이콘은 depth1 전용이므로 하위 depth 는 자기 아이콘이 아니라 조상 depth1 의 아이콘 유무를 본다.
  const renderItem = (item: SGnbMenuItem, depth: number, branchHasIcon = false): ReactNode => {
    const hasChildren = !!(item.children && item.children.length > 0);
    const isSelected = !hasChildren && item.value === value;
    const isExpanded = expandedKeys.has(item.value);
    const isHovered = hoveredKey === item.value && !item.disabled;
    const iconBranch = depth === 1 ? !!item.icon : branchHasIcon;

    const itemStyle: CSSProperties = {
      gap: M.itemGap,
      paddingTop: M.itemPaddingY,
      paddingBottom: M.itemPaddingY,
      paddingRight: M.itemPaddingRight,
      paddingLeft: DEPTH_PADDING_LEFT(M, depth),
      marginLeft: M.itemMarginX,
      marginRight: M.itemMarginX,
      borderRadius: M.itemRadius,
      backgroundColor: isSelected ? C.itemBgSelected : isHovered ? C.itemBgHover : C.itemBgDefault,
      color: isSelected ? C.itemColorActive : isHovered ? C.itemColorHover : C.itemColorDefault,
      // <button> 의 UA 폰트(Arial 13.3px)를 끊고 앱 폰트를 잇는다
      fontFamily: 'inherit',
      fontSize: depth === 1 ? M.depth1FontSize : M.itemFontSize,
      lineHeight: `${depth === 1 ? M.depth1LineHeight : M.itemLineHeight}px`,
      fontWeight: depth === 1 || isSelected ? M.depth1FontWeight : 400,
      opacity: item.disabled ? 0.4 : 1,
      cursor: item.disabled ? 'not-allowed' : 'pointer',
    };

    return (
      // li 를 flex-col 로 둬야 버튼이 좌우 마진만큼 안쪽으로 들어가고,
      // 하위 ul 은 마진 없이 전폭을 써서 depth*-paddingLeft 가 들여쓰기를 단독으로 책임진다.
      <li key={item.value} className="flex flex-col">
        <button
          type="button"
          disabled={item.disabled}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-current={isSelected ? 'page' : undefined}
          className="box-border flex select-none items-center border-none bg-transparent text-left transition-colors duration-150"
          style={itemStyle}
          onClick={() => handleItemClick(item)}
          {...rowHoverProps(item)}
        >
          {depth === 1 && item.icon && (
            <SIcon
              name={item.icon}
              size={M.depth1Icon}
              // 선택되면 라벨과 함께 활성 색으로 물든다 (기본은 전용 회색 아이콘 색)
              color={isSelected ? C.itemColorActive : C.itemIcon}
              className="shrink-0"
            />
          )}
          <span className="flex min-w-0 flex-1 items-center">
            {/* 아이콘 있는 depth1 아래에서만 하위 메뉴명 앞에 하이픈을 붙인다.
                자리표시자 높이는 행 높이를 부풀리지 않도록 line-height 에 맞춘다
                (하드코딩 24 는 belt(line-height 20)에서 행을 36px 로 밀어 올렸다 → box 36 / belt 32). */}
            {depth > 1 && iconBranch ? (
              <div
                className="mr-[8px] flex w-[20px] shrink-0 items-center justify-center"
                style={{ height: M.itemLineHeight }}
              >
                -
              </div>
            ) : null}
            {/* 말줄임은 flex 컨테이너에 걸리지 않는다(text-overflow 는 블록 컨테이너의 인라인 콘텐츠에만
                적용된다). 하이픈과 나란히 서야 하는 바깥은 flex 로 두고, 텍스트는 자기 블록에 담아
                거기서 자른다 — 폭이 좁아지거나(resizable) 메뉴명이 길면 여기서 "..." 이 된다. */}
            <span
              data-gnb-label
              className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {item.label}
            </span>
          </span>
          {item.tag !== undefined && item.tag !== '' && (
            <STag
              shape="square"
              size="xs"
              color={item.tagColor ?? 'blue'}
              label={item.tag}
              className="shrink-0"
            />
          )}
          {hasChildren && (
            <SIcon
              name="chevronDown"
              size={M.itemArrow}
              color={C.itemChevron}
              rotate={isExpanded ? 180 : 0}
              // 회전이 펼침 높이(GNB_EXPAND_MS)와 함께 끝나도록 200ms 로 맞춘다
              className="shrink-0 transition-transform duration-200"
            />
          )}
        </button>
        {/* 하위 메뉴는 열려도 닫혀도 마운트를 유지하고, 바깥 grid 의 행 크기를
            0fr↔1fr 로 전환해 높이가 부드럽게 늘고 줄게 한다(height:auto 는 전환 불가).
            안쪽 ul 은 overflow:hidden·min-height:0 으로 접히는 동안 잘려나가고,
            다 접힌 뒤엔 visibility:hidden 으로 포커스·스크린리더에서 빠진다(펼칠 땐 즉시 보임). */}
        {hasChildren && (
          <div
            className="grid"
            style={{
              gridTemplateRows: isExpanded ? '1fr' : '0fr',
              transition: `grid-template-rows ${GNB_EXPAND_MS}ms ease-out`,
            }}
          >
            <ul
              className="m-0 flex min-h-0 list-none flex-col overflow-hidden p-0"
              style={{
                visibility: isExpanded ? 'visible' : 'hidden',
                transition: `visibility 0s linear ${isExpanded ? '0s' : `${GNB_EXPAND_MS}ms`}`,
              }}
            >
              {item.children!.map(child => renderItem(child, depth + 1, iconBranch))}
            </ul>
          </div>
        )}
      </li>
    );
  };

  // useRail: items 의 첫 depth 는 레일로 빠지고, 패널에는 활성 레일의 children 이 depth1 으로 깔린다.
  const railItems = useRail ? items : [];
  const menuItems = useRail
    ? (items.find(item => item.value === activeRail)?.children ?? [])
    : items;

  // 활성 레일에 children 이 없으면 깔 메뉴가 없다 → 메뉴 nav 를 렌더하지 않고 레일만 남긴다.
  const showMenu = !useRail || menuItems.length > 0;

  // ── 메뉴 폭 (resizable) ──
  // 드래그 중에는 dragMenuWidth 로만 움직이고, 놓는 순간 한 번 밖에 알린다.
  // 매 이동마다 onMenuWidthChange 를 쏘면 앱이 그 값을 저장하는 순간 저장이 프레임마다 일어난다.
  const [uncontrolledMenuWidth, setUncontrolledMenuWidth] = useState(
    () => defaultMenuWidth ?? GNB_MENU_WIDTH,
  );
  const [dragMenuWidth, setDragMenuWidth] = useState<number | null>(null);
  const committedMenuWidth = menuWidthProp ?? uncontrolledMenuWidth;
  const menuWidth = clampGnbMenuWidth(dragMenuWidth ?? committedMenuWidth);
  const [resizing, setResizing] = useState(false);

  // 펼친 GNB 컬럼 폭. 레일이 있으면 [레일 | 메뉴] 두 칸, 메뉴가 없으면 레일 한 칸이다.
  const bodyWidth = (useRail ? GNB_RAIL_WIDTH : 0) + (showMenu ? menuWidth : 0);

  // SLayout 안이면 자기 치수·요구사항을 등록한다 — SLayout 은 GNB 내부 상수도 items·activeRail 도
  // 모르므로, 이 값으로 full 그리드의 상단바 행·메뉴 열(빈틈 방지)과 접힘 전환을 맞춘다.
  // 등록 전 폭(0)으로 한 프레임이 그려지지 않도록 layout effect 로 올린다. 단독이면 layout=null 이라 무시된다.
  const registerNav = layout?.registerNav;
  useIsomorphicLayoutEffect(() => {
    if (!registerNav) return;
    registerNav({
      columnWidth: bodyWidth,
      topHeight: H.topHeight,
      foldMs: GNB_FOLD_MS,
      requireFullHeader: railHasLeaf,
      resizing,
    });
    return () => registerNav(null);
  }, [registerNav, bodyWidth, H.topHeight, railHasLeaf, resizing]);

  // ── 메뉴 폭 조절 ──
  // 접히면 폭이 레일(fix) 또는 0(full)으로 가고, 깔 메뉴가 없으면 늘릴 대상이 없다.
  const canResize = resizable && showMenu && !folded;
  // 드래그 리스너가 리렌더 사이에도 최신 폭을 동기적으로 읽어야 한다.
  const menuWidthRef = useRef(menuWidth);
  menuWidthRef.current = menuWidth;
  const commitMenuWidth = (next: number) => {
    const clamped = clampGnbMenuWidth(next);
    setDragMenuWidth(null);
    if (menuWidthProp === undefined) setUncontrolledMenuWidth(clamped);
    if (clamped !== committedMenuWidth) onMenuWidthChange?.(clamped);
  };

  // 드래그 도중 언마운트되면 document 리스너와 body 스타일이 남는다.
  const endResizeRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endResizeRef.current?.(), []);

  const startMenuResize = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!canResize || typeof document === 'undefined') return;
    // preventDefault 로 텍스트 선택을 막으면 포커스도 따라오지 않는다.
    // 드래그를 놓은 자리에서 바로 방향키로 이어갈 수 있도록 직접 옮겨 준다.
    event.preventDefault();
    event.currentTarget.focus();

    const startX = event.clientX;
    const startWidth = menuWidthRef.current;
    let latest = startWidth;

    const { body } = document;
    const prevUserSelect = body.style.userSelect;
    const prevCursor = body.style.cursor;
    // 메뉴 텍스트가 드래그로 선택되지 않게, 커서가 페이지 위에서 바뀌지 않게 잠근다.
    body.style.userSelect = 'none';
    body.style.cursor = 'col-resize';

    const onMove = (moveEvent: MouseEvent) => {
      latest = clampGnbMenuWidth(startWidth + (moveEvent.clientX - startX));
      setDragMenuWidth(latest);
    };
    const endResize = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', endResize);
      body.style.userSelect = prevUserSelect;
      body.style.cursor = prevCursor;
      endResizeRef.current = null;
      setResizing(false);
      commitMenuWidth(latest);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', endResize);
    endResizeRef.current = endResize;
    setResizing(true);
  };

  const handleMenuResizeKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!canResize) return;
    const step = GNB_MENU_RESIZE_KEY_STEP[event.shiftKey ? 'large' : 'normal'];
    let next: number | null = null;
    if (event.key === 'ArrowLeft') next = menuWidthRef.current - step;
    else if (event.key === 'ArrowRight') next = menuWidthRef.current + step;
    // 한계는 clamp 가 안다 — 끝까지 밀어 두고 맡긴다.
    else if (event.key === 'Home') next = GNB_MENU_MIN_WIDTH;
    else if (event.key === 'End') next = GNB_MENU_MAX_WIDTH;
    if (next === null) return;

    event.preventDefault();
    commitMenuWidth(next);
  };

  const railFolded = folded && header === 'fix';

  const isFull = header === 'full';

  /**
   * full 을 **SLayout 안에서** 쓰면 루트를 display:contents 로 지워 상단바와 본문이 프레임
   * 그리드의 셀이 된다(`"header header" / "nav page"`). 상단바는 header 영역에서 전폭을 쓰고
   * 본문은 nav 영역에 서며, 페이지는 남은 page 영역에 들어온다.
   *
   * 예전엔 루트가 전 셀에 겹친 뒤 pointer-events 를 껐다 켜는 식이었다 — 상단바와 본문이 한
   * 엘리먼트에 묶여 사이에 페이지를 끼울 수 없었기 때문이다. 루트를 지우면 그 제약이 없어진다.
   *
   * 대신 루트가 없으니 루트가 하던 일을 셀이 나눠 갖는다.
   * - 배경: fix 는 루트가 칠하지만(접혀 메뉴가 사라져도 레일이 바닥까지 이어진다) full 은 상단바·메뉴가 각자 칠한다.
   * - 접힘 궤적 클리핑: fix 는 루트 overflow 가, full 은 nav 셀 래퍼가 잘라낸다.
   * - 폭: fix 는 루트가 곧 GNB 컬럼이라 루트가 전환하고, full 은 프레임의 nav 열이 전환한다.
   *
   * **프레임이 없으면(단독 렌더) 루트를 지우지 않는다.** 지우면 셀 이름을 받아줄 그리드가 없어
   * 본문이 콘텐츠 높이로 쪼그라들거나(평범한 블록 부모) 엉뚱한 칸에 자동 배치된다.
   * 이때는 루트가 전폭 세로 상자로 남아 [상단바 / 본문] 을 스스로 쌓는다.
   */
  const isFullFrame = isFull && !!layout;

  // light 패널은 배경이 페이지와 비슷해 GNB 오른쪽(페이지 경계)에 구분선을 둔다.
  // dark 는 어두운 배경으로 이미 페이지와 구분돼 선이 불필요하다.
  // fix 는 루트가 곧 GNB 컬럼이라 루트가 이 선을 그리고(상단바+본문 전체 높이),
  // full 은 루트가 전폭이라 여기 두면 페이지 맨 오른쪽에 그려지므로 메뉴 nav 가 대신 그린다.
  const rightBorder = color === 'light' ? `1px solid ${C.topBorder}` : undefined;

  /**
   * 메뉴 폭 조절선. 미끄러지는 본문 **밖**에 둬야 접힘 궤적을 따라가지 않는다.
   * 어느 상자에 거는지는 header 가 정한다 — 그 상자의 오른쪽 끝이 곧 GNB 컬럼 경계여야 하기 때문이다.
   * - fix : 루트가 곧 컬럼이다 → 루트에 걸어 **상단바 높이까지** 끌 수 있다.
   * - full: 상단바가 전폭이라 컬럼 경계는 본문 높이에만 있다 → 본문 래퍼에 건다.
   *   프레임 안이면 그 래퍼가 nav 열이라 폭이 딱 맞지만, 단독이면 전폭이라 오른쪽 끝이 컬럼 경계가
   *   아니다 → 그때만 bodyWidth 지점으로 직접 잡는다.
   * 히트 영역은 어느 상자든 접힘 궤적을 자르는 overflow:hidden 안이라 밖으로 넘길 수 없어 안쪽으로만
   * 넓힌다. 겉보기 선은 SSplitter 와 같은 --cmp-splitter-border-* 를 쓴다.
   */
  const resizeHitWidth =
    'calc(var(--cmp-splitter-border-width) + var(--cmp-splitter-border-padding) * 2)';
  const menuResizeHandle = canResize ? (
    <>
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex --
          포커스 가능한 separator = ARIA window splitter 로, 값을 가지는 위젯이다.
          jsx-a11y 는 separator 를 항상 비대화형으로 보므로 여기서만 끈다. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={`${ariaLabel} width`}
        aria-valuenow={menuWidth}
        aria-valuemin={GNB_MENU_MIN_WIDTH}
        aria-valuemax={GNB_MENU_MAX_WIDTH}
        aria-valuetext={`${menuWidth}px`}
        tabIndex={0}
        onMouseDown={startMenuResize}
        onKeyDown={handleMenuResizeKeyDown}
        className="group absolute inset-y-0 z-[1] flex cursor-col-resize justify-end outline-none"
        style={{
          width: resizeHitWidth,
          ...(isFull && !isFullFrame
            ? { left: `calc(${bodyWidth}px - ${resizeHitWidth})` }
            : { right: 0 }),
        }}
      >
        <span
          aria-hidden
          className={cn(
            // 평상시엔 자리만 잡고 칠하지 않는다 — 색이 붙는 것 자체가 조절 가능하다는 신호다.
            'w-(--cmp-splitter-border-width) transition-colors',
            'group-hover:bg-(--cmp-splitter-border-color) group-focus-visible:bg-(--cmp-splitter-border-color)',
            resizing && 'bg-(--cmp-splitter-border-color)',
          )}
        />
      </div>
      {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
    </>
  ) : null;

  const rootStyle: CSSProperties = isFullFrame
    ? { display: 'contents', ...style }
    : isFull
      ? // 단독 full — 상단바가 전폭이어야 하므로 루트도 전폭이고, 배경·선은 상단바·메뉴가 각자 그린다.
        { width: '100%', ...style }
      : {
          // fix 를 닫으면 48px 레일로 좁혀진다.
          width: folded ? GNB_COMMON.foldWidth : bodyWidth,
          // 폭을 끄는 중에는 전환을 끈다 — 접힘용 전환이 걸려 있으면 컬럼이 손보다 늦게 따라온다.
          transition: resizing ? 'none' : `width ${GNB_FOLD_MS}ms ease-out`,
          backgroundColor: C.panelBg,
          borderRight: rightBorder,
          ...style,
        };

  // 나가는 동안은 보이고 다 나간 뒤엔 포커스·스크린리더에서 빠지도록, visibility 를 접을 때만
  // 전환 길이만큼 늦춘다(펼칠 땐 0s = 즉시 보임). 언마운트·display:none 으로는 전환이 생기지 않는다.
  const hideAfterSlide = (hidden: boolean) =>
    `visibility 0s linear ${hidden ? `${GNB_FOLD_MS}ms` : '0s'}`;

  // 런처·로고 스타일: 접히면(fix 레일) 페이드하며 흐름에서 빠져(absolute)
  // 폴드 버튼만 흐름에 남는다 → 좌우 대칭 패딩 덕에 48px 레일 가운데에 선다(gap 도 흐름 아이템에만 적용).
  const sideFoldStyle: CSSProperties = {
    opacity: railFolded ? 0 : 1,
    position: railFolded ? 'absolute' : 'static',
    visibility: railFolded ? 'hidden' : 'visible',
    transition: `opacity ${GNB_FOLD_MS}ms ease-out, ${hideAfterSlide(railFolded)}`,
  };

  // 인라인 상단바의 순서는 header 가 정한다:
  // - fix : [런처? | 로고 | ...여백 | 폴드] — 상단바가 GNB 컬럼 안이라 로고가 왼쪽에 붙고
  //         폴드는 ml-auto 로 컬럼 오른쪽 끝에 고정된다(접혀도 48px 레일 폭을 꽉 채워 가운데에 선다:
  //         레일 패딩 12+12 에 xs 고스트버튼 24 = 48).
  // - full: [런처? | 폴드 | 로고 | topContent?] — 전폭 상단바라 폴드가 GNB 컬럼(메뉴) 위에 서고
  //         로고가 그 오른쪽에 이어지며, 남는 폭은 topContent 슬롯이 가져간다.
  // 간격은 컨테이너 gap 대신 각 요소 margin 으로 준다 — 순서가 header 마다 달라 흐름 위치별로 값이 다르다.
  // - fix : 런처 →16px→ 로고 →16px(최소, 나머지는 ml-auto 여백)→ 폴드
  // - full: 런처 →8px(아이콘 버튼끼리 붙는 GNB_TOP_ICON_GAP)→ 폴드 →16px→ 로고 →16px→ topContent
  // 16px 은 --cmp-gnb-top-gap(H.topGap) 이다.
  const launcherButton = onLauncherClick ? (
    <SGhostButton
      key="launcher"
      icon="launcher"
      size="xs"
      intent={color === 'dark' ? 'inverse' : 'default'}
      ariaLabel="app launcher"
      onClick={onLauncherClick}
      className="shrink-0"
      style={{ ...sideFoldStyle, marginRight: header === 'fix' ? H.topGap : GNB_TOP_ICON_GAP }}
    />
  ) : null;

  const foldButton = (
    <SGhostButton
      key="fold"
      icon="sidebar"
      size="xs"
      intent={color === 'dark' ? 'inverse' : 'default'}
      ariaLabel="toggle navigation"
      ariaPressed={folded}
      onClick={handleFoldToggle}
      className={cn('shrink-0', header === 'fix' && 'ml-auto')}
    />
  );

  const logoBox = logo ? (
    <div
      key="logo"
      className="flex shrink-0 items-center"
      style={{
        height: H.logoHeight,
        ...(header === 'full'
          ? // full 은 전폭이라 로고 자리가 고정이다 — 폭 140px(디자인 스펙, 전용 토큰 없음)로 못 박아
            // 로고 내용이 바뀌어도 오른쪽 topContent 시작점이 흔들리지 않게 한다.
            // 런처가 없으면 그 자리(버튼 + 간격)를 로고가 이어받아, 런처 유무로 topContent 가 밀리지 않는다.
            {
              width: launcherButton ? GNB_FULL_LOGO_WIDTH : GNB_FULL_LOGO_WIDTH_NO_LAUNCHER,
              marginLeft: H.topGap,
            }
          : // fix 는 폴드가 ml-auto 로 밀려나므로(margin-left 를 못 쓴다) 로고 오른쪽에 최소 간격을 둔다
            { marginRight: H.topGap }),
        ...sideFoldStyle,
      }}
    >
      {logo}
    </div>
  ) : null;

  // 로고 오른쪽 슬롯 — 로고와 16px 띄고 남는 폭을 모두 먹는다(min-w-0 으로 긴 내용이 상단바를 넓히지 않는다).
  // 전폭 상단바(full)에만 자리가 있다.
  const topContentBox =
    header === 'full' && topContent ? (
      <div
        key="topContent"
        className="flex min-w-0 flex-1 items-center"
        style={{ marginLeft: H.topGap }}
      >
        {topContent}
      </div>
    ) : null;

  // fix 의 루트는 곧 GNB 컬럼이다 — 프레임 높이를 꽉 채워(h-full) 메뉴가 페이지 바닥까지 내려오고,
  // shrink-0 으로 SPage(flex-1, basis 0)가 아니라 GNB 가 축소분을 떠안는 걸 막는다.
  // full 은 루트가 display:contents 라 상자가 없다 — 이 클래스들도, 앱이 넘긴 className 도 걸리지 않는다.
  return (
    <div
      ref={ref}
      className={cn(
        'box-border',
        // relative: fix 의 조절선이 루트(=GNB 컬럼) 오른쪽 끝을 기준으로 서야 상단바까지 이어진다.
        !isFullFrame && 'relative flex h-full shrink-0 flex-col overflow-hidden',
        className,
      )}
      style={rootStyle}
      data-type={type}
      data-header={header}
      data-color={color}
      {...rest}
    >
      {/* 상단바 — full 이면 그리드의 header 영역(전폭 1행)에 직접 앉는다.
          fix 레일이면 폴드 버튼만 남고 가운데로 미끄러진다(높이·구분선은 펼친 상태와 동일).
          런처·로고는 흐름에서 빼야(absolute) 폭이 48px 로 줄어드는 동안 폴드 버튼을 밀어내지 않는다. */}
      <div
        className="relative box-border flex shrink-0 items-center overflow-hidden"
        style={{
          ...(isFullFrame ? { gridArea: 'header' } : null),
          height: H.topHeight,
          // 간격은 컨테이너 gap 대신 각 요소 margin 으로 준다 — 순서가 header 마다 달라
          // 이웃 쌍마다 값이 다르기 때문(위 launcherButton/logoBox 주석).
          // 접히면 런처·로고가 흐름에서 빠져 margin 도 무의미해진다.
          padding: `0 ${railFolded ? GNB_RAIL.padding : H.topPaddingX}`,
          borderBottom: `1px solid ${C.topBorder}`,
          backgroundColor: C.panelBg,
          transition: `padding ${GNB_FOLD_MS}ms ease-out`,
          // 아이콘 버튼은 자체 ghostButton 토큰을 쓰므로, 이 색은 logo 슬롯 상속용
          color: C.itemColorDefault,
        }}
      >
        {/* 런처·폴드·로고·topContent 를 흐름에 나란히 둔다 — 순서는 header 가 정한다(위 주석 참고).
            없는 슬롯은 null 이라 자리를 비우지 않고 나머지가 그대로 이어 붙는다.
            접히면 런처·로고가 페이드하며 흐름에서 빠지고(sideFoldStyle), 폴드 버튼만 남아 레일 가운데에 선다. */}
        {header === 'fix' ? (
          <>
            {launcherButton}
            {logoBox}
            {foldButton}
          </>
        ) : (
          <>
            {launcherButton}
            {foldButton}
            {logoBox}
            {topContentBox}
          </>
        )}
      </div>

      {/* 접힘(fix 레일) 상단 슬롯 — 본문이 왼쪽으로 빠져나가면 rail/menu top 도 함께 사라지므로,
          48px 레일 상단(상단바 바로 아래)에 붙는 별도 슬롯(foldedTop)을 둔다.
          본문(flex-1)보다 앞이라 흐름 맨 위에 서고, 좁은 폴드 폭에 맞춰 가운데 정렬한다.
          위 여백은 아래 foldedFooter 와 대칭이다. */}
      {railFolded && foldedTop && (
        <div className="mt-[24px] flex shrink-0 items-center justify-center">{foldedTop}</div>
      )}

      {/* 본문 [레일 | 메뉴] — 접히면 제 폭만큼 왼쪽으로 미끄러져 나가고, 펼치면 반대로 들어온다.
          레일과 메뉴가 한 상자에 있어 함께 움직인다.
          바깥 래퍼가 그 궤적을 잘라낸다 — fix 는 루트 안에서 남는 높이를 먹는 칸(flex-1),
          full 은 그리드의 nav 셀(폭 전환은 프레임이 맡는다).
          안쪽 본문은 제 폭(bodyWidth)을 쥐고 있어 좁아지는 폴드 레일에 눌려 찌그러지지 않는다. */}
      <div
        className={cn('relative overflow-hidden', !isFullFrame && 'min-h-0 flex-1')}
        style={isFullFrame ? { gridArea: 'nav' } : undefined}
      >
        <div
          className="flex h-full flex-row"
          style={{
            width: bodyWidth,
            transform: folded ? 'translateX(-100%)' : 'none',
            visibility: folded ? 'hidden' : 'visible',
            transition: `transform ${GNB_FOLD_MS}ms ease-out, ${hideAfterSlide(folded)}`,
          }}
        >
          {useRail && (
            // 레일 컬럼 — 스크롤되는 아이템 목록(nav)을 가운데 두고 위아래에 고정 슬롯(top/footer)을 붙인다.
            // 두 슬롯은 내비게이션이 아니므로 nav(landmark) 밖에 두고, 컬럼 폭·구분선·배경은 이 래퍼가
            // 전체 높이에 걸쳐 책임진다(그래야 슬롯 자리까지 배경·오른쪽 선이 이어진다).
            <div
              className="flex shrink-0 flex-col overflow-hidden"
              style={{
                width: GNB_RAIL_WIDTH,
                borderRight: `1px solid ${C.topBorder}`,
                backgroundColor: C.panelBg,
              }}
            >
              {/* top 슬롯은 nav 밖이라 목록 스크롤에 딸려 올라가지 않고, shrink-0 으로 컬럼 상단에 붙는다. */}
              {railTop && <div className="shrink-0">{railTop}</div>}
              {/* 레일도 내비게이션 landmark — nav 로 세워야 aria-label(레일)이 스크린리더에 노출된다
                (role 없는 ul 의 aria-label 은 영역명으로 읽히지 않는다). 메뉴 nav 와는 레이블로 구분된다.
                padding 은 nav 안쪽에 둔다 — 남는 높이를 flex-1(min-h-0)로 채우고 넘치면 nav 에 스크롤이 생긴다.
                좁은 레일에서 스크롤바가 아이템을 가리지 않도록 스크롤은 되되 스크롤바는 숨긴다. */}
              <nav
                aria-label={`${ariaLabel} rail`}
                className={cn(
                  'flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto',
                  SCROLLBAR_HIDDEN_CLASS,
                )}
                style={{ padding: GNB_RAIL.padding }}
              >
                <ul className="m-0 flex list-none flex-col p-0" style={{ gap: GNB_RAIL.gap }}>
                  {railItems.map(renderRailItem)}
                </ul>
              </nav>
              {/* footer 는 nav 밖이라 컬럼 정렬용 좌우·하단 padding 을 직접 갖고, shrink-0 으로 컬럼 하단에 붙는다. */}
              {railFooter && <div className="shrink-0">{railFooter}</div>}
            </div>
          )}
          {showMenu && (
            <div
              className="flex shrink-0 flex-col overflow-hidden"
              style={{
                width: menuWidth,
                backgroundColor: C.panelBg,
                // full 은 루트가 오른쪽 선을 못 그리므로(전폭이거나 아예 없다) 메뉴 오른쪽(페이지 경계)이 대신 그린다.
                borderRight: isFull ? rightBorder : undefined,
              }}
            >
              {/* 메뉴 목록만 스크롤한다 — nav 가 스크롤 컨테이너다(min-h-0 flex-1 로 남는 높이를 먹고
                넘치면 스크롤). 스크롤바 숨김도 실제 스크롤이 걸리는 nav 에 붙인다(레일과 동일) —
                GNB 는 상시 노출되는 내비게이션이라 스크롤바가 메뉴 폭·정렬을 흐트러뜨리지 않게 한다.
                top/footer 는 nav 밖이라 흐름에 밀리지 않고 shrink-0 으로 위아래에 붙는다. */}
              {menuTop && <div className="shrink-0">{menuTop}</div>}
              <nav
                aria-label={ariaLabel}
                className={cn(
                  'flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto',
                  SCROLLBAR_HIDDEN_CLASS,
                )}
                style={{
                  paddingTop: M.bodyPaddingY,
                  paddingBottom: M.bodyPaddingY,
                }}
              >
                <ul className="m-0 flex list-none flex-col p-0">
                  {menuItems.map(item => renderItem(item, 1))}
                </ul>
              </nav>
              {menuFooter && <div className="shrink-0">{menuFooter}</div>}
            </div>
          )}
        </div>

        {/* full 은 상단바가 전폭이라 컬럼 경계가 본문 높이에만 있다 → 조절선도 이 칸에 선다. */}
        {header === 'full' && menuResizeHandle}
      </div>

      {/* 접힘(fix 레일) 하단 슬롯 — 본문이 왼쪽으로 빠져나가면 rail/menu footer 도 함께 사라지므로,
          48px 레일 바닥에 붙는 별도 슬롯(foldedFooter)을 둔다. 본문(flexGrow:1)이 남는 높이를 먹어 바닥으로 민다.
          좁은 폴드 폭에 맞춰 가운데 정렬한다. */}
      {/* fix 는 루트가 곧 GNB 컬럼이다 → 조절선을 여기 걸어 상단바 높이까지 끌 수 있게 한다. */}
      {header === 'fix' && menuResizeHandle}

      {/* 잘린 메뉴명의 전체 이름. GNB 당 하나이고 virtualRef 로 hover 한 라벨을 가리킨다
          (Anchor 는 virtualRef 를 주면 DOM 을 만들지 않아 display:contents 루트에도 안전하다).
          메뉴가 좁은 왼쪽 열이라 오른쪽으로 띄운다 — 위로 띄우면 바로 윗 메뉴를 가린다. */}
      <RPopover.Root open={labelTip !== null} onOpenChange={open => !open && setLabelTip(null)}>
        <RPopover.Anchor virtualRef={labelTipAnchorRef} />
        <RPopover.Portal container={portalContainer}>
          <RPopover.Content
            side="right"
            sideOffset={4}
            className={cn('z-50', FLOATING_SLIDE_ANIM)}
            aria-hidden
            // 포커스를 가로채지 않는다 (라벨 칩은 읽기 전용 표시다)
            onOpenAutoFocus={event => event.preventDefault()}
            // 칩 위로 마우스가 넘어와도 닫히지 않게 — 라벨과 칩 사이 4px 를 건너는 동안의 유예다
            onMouseEnter={cancelLabelTipHide}
            onMouseLeave={startLabelTipHide}
          >
            <STag shape="square" size="xs" color="grey" label={labelTip?.label ?? ''} />
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>

      {railFolded && foldedFooter && (
        <div className="flex shrink-0 items-center justify-center mb-[24px]">{foldedFooter}</div>
      )}
    </div>
  );
});
